import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { localDayToUtcIso } from '../utils/calendar-day.util';

export interface PlanWorkReference {
  assetId: string;
  repairIds?: string[];
  protectionIds?: string[];
}

/**
 * Tells Plan Management that a repair or protection saved in ThreeD was executed, or no
 * longer is. ThreeD stays the record of the work; the plan's item follows it.
 *
 * Best effort: a failed call is not retried or shown. Closeout approval asks ThreeD about
 * every repair still outlined, which catches it there.
 */
@Injectable({
  providedIn: 'root',
})
export class PlanExecutionService {
  private readonly base = `${environment.API_INSPEC}v2/plans/work/execution`;

  constructor(private http: HttpClient) {}

  async recordExecuted(work: PlanWorkReference, executedOn: Date | string): Promise<void> {
    await this.post(this.base, {
      AssetId: work.assetId,
      RepairIds: work.repairIds ?? [],
      ProtectionIds: work.protectionIds ?? [],
      ExecutedAtUtc: localDayToUtcIso(executedOn),
      // One id per save: executing again after an untick is a new execution, not a
      // replay of the first one.
      ExecutionEventId: crypto.randomUUID(),
    });
  }

  async withdrawExecuted(work: PlanWorkReference): Promise<void> {
    await this.post(`${this.base}/withdraw`, {
      AssetId: work.assetId,
      RepairIds: work.repairIds ?? [],
      ProtectionIds: work.protectionIds ?? [],
    });
  }

  private async post(url: string, body: unknown): Promise<void> {
    try {
      await firstValueFrom(this.http.post(url, body));
    } catch (error) {
      // Work outside any plan answers 404, a reviewed plan 409: both are expected.
      console.warn('Plan Management was not updated for this work', error);
    }
  }
}
